<template>
  <div class="cl-wrap">
    <div v-if="!isIdiaContext" class="cl-guard">
      <strong>Bu sayfa sadece Altında Fırsat / Idia için kullanılabilir.</strong>
      <p>Üst menüden tenant <code>Altında Fırsat</code> ve marketplace <code>Idia</code> seçili olmalı.</p>
    </div>

    <template v-else>
      <header class="cl-header">
        <h1>Kargo Bilgileri</h1>
        <p class="cl-sub">YK'ya gönderilmiş kargoların listesi ve güncel durumu.</p>
      </header>

      <nav class="cl-tabs">
        <button :class="['cl-tab', activeTab === 'all' ? 'cl-tab-active' : '']" @click="switchTab('all')">
          Tüm Kargolar
        </button>
        <button :class="['cl-tab', activeTab === 'overdue' ? 'cl-tab-active' : '']" @click="switchTab('overdue')">
          Tarihi Geçenler
          <span v-if="overdueTotal > 0" class="cl-tab-badge">{{ overdueTotal }}</span>
        </button>
      </nav>

      <section v-show="activeTab === 'all'" class="cl-filters">
        <div class="cl-filter">
          <label>Arama</label>
          <input v-model="filters.search" type="text" placeholder="Barkod, alıcı, YK gönderi no..." @keydown.enter="loadList(1)">
        </div>
        <div class="cl-filter">
          <label>Durum</label>
          <select v-model="filters.status">
            <option value="">Tümü</option>
            <option value="Failed">YK'ya gönderilmemiş (hatalı veya hiç denenmemiş)</option>
            <option value="NOP">İşlem Görmemiş</option>
            <option value="ISR">İrsaliye Düzenlendi</option>
            <option value="IND">Teslimatta</option>
            <option value="DLV">Teslim Edildi</option>
            <option value="CNL">İptal Edildi</option>
            <option value="ISC">Daha Önce İptal</option>
            <option value="BI">Fatura İptal</option>
          </select>
        </div>
        <div class="cl-filter">
          <label>Başlangıç</label>
          <input v-model="filters.from" type="date">
        </div>
        <div class="cl-filter">
          <label>Bitiş</label>
          <input v-model="filters.to" type="date">
        </div>
        <div class="cl-filter">
          <button class="cl-btn primary" :disabled="loading" @click="loadList(1)">{{ loading ? '...' : 'Filtrele' }}</button>
        </div>
        <div class="cl-filter">
          <button class="cl-btn ghost" :disabled="loading" @click="toggleDateRange" :title="weekActive ? 'Bugünün kargolarını listele' : 'Son 7 günün kargolarını listele'">
            📅 {{ weekActive ? 'Bugün' : 'Son 1 Hafta' }}
          </button>
        </div>
        <label class="cl-only-lost" title="Tikliyse tarih filtresi yok sayılır, tüm kayıp işaretli kargolar gelir">
          <input type="checkbox" v-model="filters.onlyLost" @change="loadList(1)">
          <span>🚨 Sadece Kayıplar</span>
        </label>
        <div v-if="syncButtonVisible" class="cl-filter">
          <button class="cl-btn ghost" :disabled="syncingAll || loading" @click="runSyncNow" title="YK'ya hemen sorgu at ve tüm kargo durumlarını/iade alanlarını güncelle">
            🔄 {{ syncingAll ? 'Sync...' : 'Şimdi Sync Yap' }}
          </button>
        </div>
      </section>

      <section v-show="activeTab === 'all'" class="cl-table-wrap">
        <table class="cl-table">
          <thead>
            <tr>
              <th>Barkod</th>
              <th>Paket No</th>
              <th>Alıcı</th>
              <th>Şehir</th>
              <th>Gönderim</th>
              <th>YK Durumu</th>
              <th>Son Sorgu</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="items.length === 0">
              <td colspan="8" class="cl-empty">Kayıt bulunamadı.</td>
            </tr>
            <template v-for="row in items">
              <tr :key="row.orderId" :class="{ 'cl-row-selected': !!expanded[row.orderId], 'cl-row-failed': isRowFailed(row), 'cl-row-lost': row.ykIsLost === true }">
                <td class="mono">{{ row.barcode }}</td>
                <td class="mono">{{ row.packageNumber || '—' }}</td>
                <td>{{ row.recipientName }}</td>
                <td>{{ row.city }}</td>
                <td>{{ formatDate(row.ykSentAt) }}</td>
                <td>
                  <span :class="['cl-status', statusClass(row.ykOperationStatus, row)]">{{ statusLabel(row) }}</span>
                  <div v-if="isRowFailed(row)" class="cl-row-err" :title="row.ykErrorMessage">{{ row.ykErrorMessage || 'YK\'ya hiç gönderilmemiş' }}</div>
                </td>
                <td>{{ row.ykLastSyncAt ? formatDate(row.ykLastSyncAt) : '—' }}</td>
                <td>
                  <div class="cl-row-actions">
                    <button
                      :class="['cl-btn', expanded[row.orderId] ? 'ghost-active' : 'ghost']"
                      @click="toggleDetail(row.orderId)"
                    >{{ expanded[row.orderId] ? 'Detayı Kapat' : 'Detay' }}</button>
                    <button
                      v-if="isRowFailed(row)"
                      class="cl-btn primary cl-retry"
                      :disabled="retryingId === row.orderId"
                      @click="retryRow(row)"
                    >{{ retryingId === row.orderId ? '...' : '🔄 Tekrar Dene' }}</button>
                    <button
                      :class="['cl-btn', isCancelable(row) ? 'danger' : 'disabled', 'cl-cancel']"
                      :disabled="!isCancelable(row)"
                      :title="isCancelable(row) ? 'Kargoyu iptal et' : 'Bu durumda iptal edilemez'"
                      @click="openCancelModal(row)"
                    >İptal Et</button>
                    <button
                      :class="['cl-btn', canMarkReturned(row) ? 'amber' : 'disabled']"
                      :disabled="!canMarkReturned(row)"
                      :title="canMarkReturned(row) ? 'Kargo şirketi teslim edemeyip geri gönderdiyse, sistem otomatik tespit etmediği durumda manuel işaretleyin (müşteri iadesi DEĞİL)' : (row.ykIsReturned === true ? 'Bu kargo zaten geri dönüş olarak işaretli' : 'Bu kargo final statüde (Teslim Edildi / İptal Edildi), geri dönüş işaretlemesi yapılamaz')"
                      @click="openMarkReturnedModal(row)"
                    >↩️ Kargo Geri Döndü</button>
                    <button
                      :class="['cl-btn', isReshipable(row) ? 'warning' : 'disabled', 'cl-reship']"
                      :disabled="!isReshipable(row)"
                      :title="reshipTooltip(row)"
                      @click="openReshipModal(row)"
                    >🔁 Yeni Barkod</button>
                    <button
                      :class="['cl-btn', row.ykIsLost === true ? 'lost-active' : 'lost']"
                      :title="row.ykIsLost === true ? 'Bu kargo kayıp olarak işaretli — geri al' : 'Bu kargoyu kayıp olarak işaretle'"
                      @click="openLostModal(row)"
                    >🚨 {{ row.ykIsLost === true ? 'Kayıp Kaldır' : 'Kayıp' }}</button>
                  </div>
                </td>
              </tr>
              <tr v-if="expanded[row.orderId]" :key="row.orderId + '-d'" class="cl-expanded-row">
                <td :colspan="8">
                  <div v-if="expanded[row.orderId].loading" class="cl-detail-state">Detay yükleniyor...</div>
                  <div v-else-if="expanded[row.orderId].error" class="cl-detail-state cl-err">{{ expanded[row.orderId].error }}</div>
                  <div v-else-if="expanded[row.orderId].data" class="cl-detail-inline">
                    <header class="cl-detail-head">
                      <div class="cl-detail-title">
                        <span class="mono cl-detail-barcode">{{ expanded[row.orderId].data.barcode }}</span>
                        <span class="cl-detail-sep">·</span>
                        <span>Detay</span>
                      </div>
                      <div class="cl-detail-actions">
                        <button
                          :class="['cl-btn', isFinal(expanded[row.orderId].data.ykOperationStatus) ? 'disabled' : 'primary-solid']"
                          :disabled="expanded[row.orderId].syncing || isFinal(expanded[row.orderId].data.ykOperationStatus)"
                          :title="isFinal(expanded[row.orderId].data.ykOperationStatus) ? 'Bu kargo final statüde, durum değişmez' : ''"
                          @click="syncRow(row.orderId)"
                        >{{ expanded[row.orderId].syncing ? 'Yenileniyor...' : '🔄 YK\'dan Yenile' }}</button>
                        <button class="cl-btn ghost" @click="toggleDetail(row.orderId)">Kapat ✕</button>
                      </div>
                    </header>

                    <div class="cl-detail-grid">
                      <div><label>Sipariş ID</label><span>#{{ expanded[row.orderId].data.orderId }}</span></div>
                      <div><label>Alıcı</label><span>{{ expanded[row.orderId].data.recipientName }}</span></div>
                      <div><label>Telefon</label><span>{{ expanded[row.orderId].data.phone }}</span></div>
                      <div><label>Şehir</label><span>{{ expanded[row.orderId].data.city }}</span></div>
                      <div><label>Varış Şubesi</label><span>{{ expanded[row.orderId].data.ykArrivalUnitName || '—' }}</span></div>
                      <div class="cl-full"><label>Adres</label><span>{{ expanded[row.orderId].data.shippingAddress }}</span></div>
                      <div><label>YK Talep (jobId)</label><span class="mono">{{ expanded[row.orderId].data.ykJobId || '—' }}</span></div>
                      <div><label>YK Gönderi (docId)</label><span class="mono">{{ expanded[row.orderId].data.ykDocId || '—' }}</span></div>
                      <div><label>YK Durum</label><span :class="['cl-status', statusClass(expanded[row.orderId].data.ykOperationStatus, expanded[row.orderId].data)]" :title="expanded[row.orderId].data.ykIsReturned ? 'Ham YK durumu: ' + statusText(expanded[row.orderId].data.ykOperationStatus) : ''">{{ statusLabel(expanded[row.orderId].data) }}</span></div>
                      <div><label>Durum Açıklaması</label><span>{{ expanded[row.orderId].data.ykOperationMessage || '—' }}</span></div>
                      <div><label>Gönderim</label><span>{{ formatDate(expanded[row.orderId].data.ykSentAt) }}</span></div>
                      <div><label>Son Sorgu</label><span>{{ expanded[row.orderId].data.ykLastSyncAt ? formatDate(expanded[row.orderId].data.ykLastSyncAt) : '—' }}</span></div>
                      <div v-if="expanded[row.orderId].data.ykErrorMessage" class="cl-full"><label>Son Hata</label><span class="cl-err">{{ expanded[row.orderId].data.ykErrorMessage }}</span></div>
                      <div v-if="expanded[row.orderId].data.ykIsReturned || expanded[row.orderId].data.ykRejectFlag === '1' || expanded[row.orderId].data.ykRejectStatus" class="cl-full">
                        <label>İade Bilgisi</label>
                        <span class="cl-return-info">
                          <span class="cl-return-badge">↩️ Kargo Geri Dönüşü</span>
                          <span v-if="expanded[row.orderId].data.ykIsReturnedAuto === true" class="cl-return-tag cl-return-tag-auto">Otomatik Tespit</span>
                          <span v-else-if="expanded[row.orderId].data.ykIsReturnedAuto === false" class="cl-return-tag cl-return-tag-manual">Manuel İşaretli</span>
                          <span v-if="expanded[row.orderId].data.ykRejectFlag" class="cl-return-meta">rejectFlag: {{ expanded[row.orderId].data.ykRejectFlag }}</span>
                          <span v-if="expanded[row.orderId].data.ykRejectStatusLabel" class="cl-return-meta">Aşama: {{ expanded[row.orderId].data.ykRejectStatusLabel }}{{ expanded[row.orderId].data.ykRejectStatus ? ' (' + expanded[row.orderId].data.ykRejectStatus + ')' : '' }}</span>
                          <span v-if="expanded[row.orderId].data.ykRejectReasonExplanation" class="cl-return-meta">Neden: {{ expanded[row.orderId].data.ykRejectReasonExplanation }}</span>
                        </span>
                      </div>
                      <div v-if="expanded[row.orderId].data.trackingUrl" class="cl-full">
                        <label>Takip Linki</label>
                        <a :href="expanded[row.orderId].data.trackingUrl" target="_blank" class="cl-link">{{ expanded[row.orderId].data.trackingUrl }}</a>
                      </div>
                    </div>

                    <div v-if="expanded[row.orderId].lastSync && expanded[row.orderId].lastSync.history && expanded[row.orderId].lastSync.history.length > 0" class="cl-history">
                      <h3>Taşıma Hareketleri</h3>
                      <table class="cl-history-table">
                        <thead>
                          <tr><th>Tarih</th><th>Birim</th><th>Olay</th><th>Açıklama</th><th>Şehir/İlçe</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="(h, i) in expanded[row.orderId].lastSync.history" :key="i">
                            <td>{{ formatYkDateTime(h.eventDate, h.eventTime) }}</td>
                            <td>{{ h.unitName }}</td>
                            <td>{{ h.eventName }}</td>
                            <td>{{ h.reasonName }}</td>
                            <td>{{ h.cityName }}{{ h.townName ? ' / ' + h.townName : '' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="total > pageSize" class="cl-pagination">
          <button class="cl-btn ghost" :disabled="page === 1" @click="loadList(page - 1)">◄ Önceki</button>
          <span>Sayfa {{ page }} / {{ Math.ceil(total / pageSize) }} (Toplam: {{ total }})</span>
          <button class="cl-btn ghost" :disabled="page * pageSize >= total" @click="loadList(page + 1)">Sonraki ►</button>
        </div>
      </section>

      <section v-show="activeTab === 'overdue'" class="cl-filters">
        <div class="cl-filter">
          <label>Arama</label>
          <input v-model="overdueFilters.search" type="text" placeholder="Barkod, alıcı, şehir..." @keydown.enter="loadOverdue(1)">
        </div>
        <div class="cl-filter">
          <button class="cl-btn primary" :disabled="overdueLoading" @click="loadOverdue(1)">{{ overdueLoading ? '...' : 'Filtrele' }}</button>
        </div>
        <div class="cl-filter">
          <button class="cl-btn ghost" :disabled="overdueLoading" @click="loadOverdue(overduePage)" title="Listeyi yenile">
            🔄 Yenile
          </button>
        </div>
      </section>

      <div v-show="activeTab === 'overdue' && overdueUnmatchedCount > 0" class="cl-overdue-warn">
        <span>⚠ {{ overdueUnmatchedCount }} aktif sipariş "DeliveryCities" tablosunda eşleşmedi; bunların gecikmesi hesaplanamadı. Eksik şehirleri ekleyin.</span>
      </div>

      <section v-show="activeTab === 'overdue'" class="cl-table-wrap">
        <table class="cl-table">
          <thead>
            <tr>
              <th>Barkod</th>
              <th>Paket No</th>
              <th>Sipariş</th>
              <th>Alıcı</th>
              <th>Şehir</th>
              <th>Gönderim</th>
              <th>Beklenen Tarih</th>
              <th>Süre</th>
              <th>Gecikme</th>
              <th>YK Durumu</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="overdueItems.length === 0 && !overdueLoading">
              <td colspan="11" class="cl-empty">Tarihi geçmiş kargo bulunamadı.</td>
            </tr>
            <tr v-for="row in overdueItems" :key="row.orderId" :class="['cl-row-overdue', $route.query.orderId && Number($route.query.orderId) === row.orderId ? 'cl-row-highlight' : '']">
              <td class="mono">{{ row.barcode }}</td>
              <td class="mono">{{ row.packageNumber || '—' }}</td>
              <td>#{{ row.orderId }}</td>
              <td>{{ row.recipientName }}</td>
              <td>{{ row.city }}</td>
              <td>{{ formatDate(row.ykSentAt) }}</td>
              <td>{{ formatDate(row.expectedDeliveryDate) }}</td>
              <td>{{ row.cityDuration }} gün</td>
              <td>
                <span class="cl-overdue-badge">{{ row.daysOverdue }} gün</span>
              </td>
              <td>
                <span :class="['cl-status', statusClass(row.ykOperationStatus, row)]">{{ statusText(row.ykOperationStatus) || 'İşlem Görmemiş' }}</span>
              </td>
              <td>
                <button class="cl-btn ghost" @click="openInAll(row)">Detayda Aç</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="overdueTotal > overduePageSize" class="cl-pagination">
          <button class="cl-btn ghost" :disabled="overduePage === 1" @click="loadOverdue(overduePage - 1)">◄ Önceki</button>
          <span>Sayfa {{ overduePage }} / {{ Math.ceil(overdueTotal / overduePageSize) }} (Toplam: {{ overdueTotal }})</span>
          <button class="cl-btn ghost" :disabled="overduePage * overduePageSize >= overdueTotal" @click="loadOverdue(overduePage + 1)">Sonraki ►</button>
        </div>
      </section>

      <div v-if="cancelModal.open" class="cl-modal-backdrop" @click.self="closeCancelModal">
        <div class="cl-modal">
          <header class="cl-modal-head">
            <h3>Kargo İptali — {{ cancelModal.barcode }}</h3>
            <button class="cl-btn ghost" :disabled="cancelModal.busy" @click="closeCancelModal">✕</button>
          </header>

          <div class="cl-modal-body">
            <p class="cl-warn">⚠ Bu işlem geri alınamaz. YK kargo gönderimi iptal edilir, sipariş ve ürün durumları "İptal Edildi" olarak işaretlenir.</p>

            <div v-if="cancelModal.step === 1">
              <label class="cl-modal-label">Onay için kargo barkodunu yazın:</label>
              <input
                ref="cancelBarcodeInput"
                v-model="cancelModal.input"
                type="text"
                class="cl-modal-input"
                :placeholder="cancelModal.barcode"
                :disabled="cancelModal.busy"
                @keydown.enter.prevent="validateBarcode"
              >
              <p v-if="cancelModal.error" class="cl-modal-err">{{ cancelModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="cancelModal.busy" @click="closeCancelModal">Vazgeç</button>
                <button class="cl-btn primary" :disabled="!cancelModal.input || cancelModal.busy" @click="validateBarcode">Devam Et</button>
              </div>
            </div>

            <div v-else-if="cancelModal.step === 2">
              <p>Barkod doğrulandı: <strong class="mono">{{ cancelModal.barcode }}</strong></p>
              <p><strong>Bu kargoyu iptal etmek istediğinize emin misiniz?</strong></p>
              <p v-if="cancelModal.error" class="cl-modal-err">{{ cancelModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="cancelModal.busy" @click="closeCancelModal">Vazgeç</button>
                <button class="cl-btn danger" :disabled="cancelModal.busy" @click="confirmCancel">
                  {{ cancelModal.busy ? 'İptal ediliyor...' : 'Evet, İptal Et' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="reshipModal.open" class="cl-modal-backdrop" @click.self="closeReshipModal">
        <div class="cl-modal">
          <header class="cl-modal-head" style="background:#fff7ed">
            <h3 style="color:#9a3412">Yeni Barkod Oluştur — {{ reshipModal.barcode }}</h3>
            <button class="cl-btn ghost" :disabled="reshipModal.busy" @click="closeReshipModal">✕</button>
          </header>
          <div class="cl-modal-body">
            <p class="cl-warn">Bu sipariş iade ile geri döndü. Yeni barkod oluşturulup YK'ya tekrar gönderilecek. Mevcut barkod geçmişe kaydedilir.</p>
            <div v-if="reshipModal.step === 1">
              <label class="cl-modal-label">Onay için mevcut barkodu yazın:</label>
              <input
                ref="reshipBarcodeInput"
                v-model="reshipModal.input"
                type="text"
                class="cl-modal-input"
                :placeholder="reshipModal.barcode"
                :disabled="reshipModal.busy"
                @keydown.enter.prevent="validateReshipBarcode"
              >
              <p v-if="reshipModal.error" class="cl-modal-err">{{ reshipModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="reshipModal.busy" @click="closeReshipModal">Vazgeç</button>
                <button class="cl-btn primary" :disabled="!reshipModal.input || reshipModal.busy" @click="validateReshipBarcode">Devam Et</button>
              </div>
            </div>
            <div v-else-if="reshipModal.step === 2">
              <p>Barkod doğrulandı: <strong class="mono">{{ reshipModal.barcode }}</strong></p>
              <p>Bu kargo için yeni barkod oluşturulacak ve YK'ya yeniden gönderim yapılacak. Onaylıyor musunuz?</p>
              <p v-if="reshipModal.error" class="cl-modal-err">{{ reshipModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="reshipModal.busy" @click="closeReshipModal">Vazgeç</button>
                <button class="cl-btn warning" :disabled="reshipModal.busy" @click="confirmReship">
                  {{ reshipModal.busy ? 'Gönderiliyor...' : 'Evet, Yeni Barkod Oluştur' }}
                </button>
              </div>
            </div>
            <div v-else-if="reshipModal.step === 3">
              <div class="cl-success">
                <div class="cl-success-icon">✓</div>
                <h4>Yeni Barkod Oluşturuldu</h4>
                <p>Eski barkod: <span class="mono">{{ reshipModal.barcode }}</span></p>
                <p>Yeni barkod: <strong class="mono cl-new-barcode">{{ reshipModal.newBarcode }}</strong></p>
              </div>
              <p class="cl-modal-label" style="margin-top:12px">Yeni barkod etiketini şimdi yazdırmak ister misiniz?</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" @click="closeReshipModal">Daha Sonra</button>
                <button class="cl-btn primary" @click="printNewBarcode">🖨️ Barkodu Yazdır</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="markReturnedModal.open" class="cl-modal-backdrop" @click.self="closeMarkReturnedModal">
        <div class="cl-modal">
          <header class="cl-modal-head" style="background:#fef3c7">
            <h3 style="color:#92400e">↩️ Kargo Geri Dönüşü — Manuel İşaretleme ({{ markReturnedModal.barcode }})</h3>
            <button class="cl-btn ghost" :disabled="markReturnedModal.busy" @click="closeMarkReturnedModal">✕</button>
          </header>
          <div class="cl-modal-body">
            <p class="cl-warn">
              Bu işaretleme, <strong>kargo şirketinin (Yurtiçi Kargo) siparişi teslim edemeyip bize geri göndermesi</strong> durumudur.
              Sistem otomatik tespit etmediği durumlarda (YK ekibinden telefonla/mailden bilgi geldiğinde) manuel olarak işaretleyin.
              <br><br>
              <strong>⚠ Müşteri iadesi DEĞİLDİR.</strong> Altın satışı kesin satıştır; bu seçenek yalnızca kargonun teslim edilemeyip geri dönmesi içindir.
              <br><br>
              İşaretleme sonrası "Yeni Barkod Oluştur" butonu aktif olur ve yeniden gönderim yapılabilir.
            </p>
            <div v-if="markReturnedModal.step === 1">
              <label class="cl-modal-label">Onay için kargo barkodunu yazın:</label>
              <input
                ref="markReturnedBarcodeInput"
                v-model="markReturnedModal.input"
                type="text"
                class="cl-modal-input"
                :placeholder="markReturnedModal.barcode"
                :disabled="markReturnedModal.busy"
                @keydown.enter.prevent="validateMarkReturnedBarcode"
              >
              <p v-if="markReturnedModal.error" class="cl-modal-err">{{ markReturnedModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="markReturnedModal.busy" @click="closeMarkReturnedModal">Vazgeç</button>
                <button class="cl-btn primary" :disabled="!markReturnedModal.input || markReturnedModal.busy" @click="validateMarkReturnedBarcode">Devam Et</button>
              </div>
            </div>
            <div v-else-if="markReturnedModal.step === 2">
              <p>Barkod doğrulandı: <strong class="mono">{{ markReturnedModal.barcode }}</strong></p>
              <p><strong>Bu kargonun, Yurtiçi Kargo tarafından teslim edilemeyip tarafımıza geri döndüğünü onaylıyor musunuz?</strong></p>
              <p v-if="markReturnedModal.error" class="cl-modal-err">{{ markReturnedModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="markReturnedModal.busy" @click="closeMarkReturnedModal">Vazgeç</button>
                <button class="cl-btn amber" :disabled="markReturnedModal.busy" @click="confirmMarkReturned">
                  {{ markReturnedModal.busy ? 'İşaretleniyor...' : 'Evet, Kargo Geri Döndü' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="lostModal.open" class="cl-modal-backdrop" @click.self="closeLostModal">
        <div class="cl-modal">
          <header class="cl-modal-head" style="background:#fee2e2">
            <h3 style="color:#991b1b">🚨 {{ lostModal.isLost ? 'Kayıp İşaretini Kaldır' : 'Kayıp Olarak İşaretle' }} — {{ lostModal.barcode }}</h3>
            <button class="cl-btn ghost" :disabled="lostModal.busy" @click="closeLostModal">✕</button>
          </header>
          <div class="cl-modal-body">
            <p class="cl-warn" v-if="!lostModal.isLost">
              Bu kargonun <strong>kayıp olduğunu</strong> işaretlemek üzeresiniz. İşaretleme sonrası satır açık kırmızı renkte gösterilir ve "Sadece Kayıplar" filtresinden listelenebilir.
            </p>
            <p class="cl-warn" v-else>
              Bu kargonun kayıp işaretini <strong>kaldırmak</strong> üzeresiniz.
            </p>
            <div v-if="lostModal.step === 1">
              <label class="cl-modal-label">Onay için kargo barkodunu yazın:</label>
              <input
                ref="lostBarcodeInput"
                v-model="lostModal.input"
                type="text"
                class="cl-modal-input"
                :placeholder="lostModal.barcode"
                :disabled="lostModal.busy"
                @keydown.enter.prevent="validateLostBarcode"
              >
              <p v-if="lostModal.error" class="cl-modal-err">{{ lostModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="lostModal.busy" @click="closeLostModal">Vazgeç</button>
                <button class="cl-btn primary" :disabled="!lostModal.input || lostModal.busy" @click="validateLostBarcode">Devam Et</button>
              </div>
            </div>
            <div v-else-if="lostModal.step === 2">
              <p>Barkod doğrulandı: <strong class="mono">{{ lostModal.barcode }}</strong></p>
              <p><strong>{{ lostModal.isLost ? 'Kayıp işaretini kaldırmayı onaylıyor musunuz?' : 'Bu kargonun kayıp olduğunu onaylıyor musunuz?' }}</strong></p>
              <p v-if="lostModal.error" class="cl-modal-err">{{ lostModal.error }}</p>
              <div class="cl-modal-actions">
                <button class="cl-btn ghost" :disabled="lostModal.busy" @click="closeLostModal">Vazgeç</button>
                <button class="cl-btn danger" :disabled="lostModal.busy" @click="confirmSetLost">
                  {{ lostModal.busy ? 'İşleniyor...' : (lostModal.isLost ? 'Evet, İşareti Kaldır' : 'Evet, Kayıp İşaretle') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import yurticiKargoApi from '@/api/yurticiKargo';

export default {
  name: 'CargoListPage',
  data() {
    const today = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    return {
      loading: false,
      items: [],
      total: 0,
      page: 1,
      pageSize: 50,
      filters: { search: '', status: '', from: today, to: today, onlyLost: false },
      weekActive: false,
      syncingAll: false,
      syncButtonVisible: false,
      secretBuffer: '',
      secretTimer: null,
      expanded: {},
      retryingId: null,
      activeTab: 'all',
      overdueItems: [],
      overdueTotal: 0,
      overduePage: 1,
      overduePageSize: 50,
      overdueLoading: false,
      overdueLoaded: false,
      overdueUnmatchedCount: 0,
      overdueFilters: { search: '' },
      cancelModal: { open: false, step: 1, orderId: null, barcode: '', input: '', error: '', busy: false },
      reshipModal: { open: false, step: 1, orderId: null, barcode: '', input: '', error: '', busy: false, newBarcode: '', printData: null },
      markReturnedModal: { open: false, step: 1, orderId: null, barcode: '', input: '', error: '', busy: false },
      lostModal: { open: false, step: 1, orderId: null, barcode: '', input: '', error: '', busy: false, isLost: false },
      selectedTenantId: parseInt(localStorage.getItem('selectedTenantId') || '0', 10),
      selectedMpId: parseInt(localStorage.getItem('selectedMpId') || '0', 10)
    };
  },
  computed: {
    isIdiaContext() {
      return this.selectedTenantId === 1 && this.selectedMpId === 5;
    }
  },
  mounted() {
    if (!this.isIdiaContext) {
      window.addEventListener('keydown', this.handleSecretKey);
      return;
    }
    const queryTab = this.$route.query.tab;
    if (queryTab === 'overdue') {
      this.activeTab = 'overdue';
      this.loadOverdue(1);
      this.loadList(1);
    } else {
      this.loadList(1);
      this.loadOverdue(1);
    }
    window.addEventListener('keydown', this.handleSecretKey);
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (!this.isIdiaContext) return;
      const next = newTab === 'overdue' ? 'overdue' : 'all';
      if (next !== this.activeTab) this.switchTab(next, true);
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleSecretKey);
    if (this.secretTimer) clearTimeout(this.secretTimer);
  },
  methods: {
    isoDate(d) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    switchTab(tab, skipRouteUpdate) {
      this.activeTab = tab;
      if (tab === 'overdue' && !this.overdueLoaded) this.loadOverdue(1);
      if (!skipRouteUpdate) {
        const query = tab === 'overdue' ? { tab: 'overdue' } : {};
        if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
          this.$router.replace({ path: this.$route.path, query }).catch(() => {});
        }
      }
    },
    async loadOverdue(page) {
      if (!this.isIdiaContext) return;
      this.overdueLoading = true;
      this.overduePage = page;
      try {
        const params = { page, pageSize: this.overduePageSize };
        if (this.overdueFilters.search) params.search = this.overdueFilters.search;
        const r = await yurticiKargoApi.overdueList(params);
        const d = r.data || {};
        this.overdueItems = d.items || [];
        this.overdueTotal = d.total || 0;
        this.overdueUnmatchedCount = d.unmatchedCityCount || 0;
        this.overdueLoaded = true;
      } catch (e) {
        this.overdueItems = [];
        this.overdueTotal = 0;
        this.overdueUnmatchedCount = 0;
      } finally {
        this.overdueLoading = false;
      }
    },
    async openInAll(row) {
      this.activeTab = 'all';
      this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {});
      this.filters.search = row.barcode || '';
      this.filters.status = '';
      this.filters.from = '';
      this.filters.to = '';
      this.weekActive = false;
      await this.loadList(1);
      if (this.items.some(i => i.orderId === row.orderId)) {
        this.toggleDetail(row.orderId);
      }
    },
    handleSecretKey(e) {
      if (this.syncButtonVisible) return;
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!e.key || e.key.length !== 1) return;
      this.secretBuffer = (this.secretBuffer + e.key.toLowerCase()).slice(-30);
      if (this.secretTimer) clearTimeout(this.secretTimer);
      this.secretTimer = setTimeout(() => { this.secretBuffer = ''; }, 2000);
      if (this.secretBuffer.includes('mahmutaltunkaya')) {
        this.syncButtonVisible = true;
        this.secretBuffer = '';
      }
    },
    async runSyncNow() {
      if (this.syncingAll) return;
      this.syncingAll = true;
      try {
        const r = await yurticiKargoApi.runSyncNow();
        const d = r.data || {};
        await this.loadList(this.page);
        const scanned = d.scanned != null ? d.scanned : 0;
        const statusChanged = d.statusChanged != null ? d.statusChanged : 0;
        const idiaNotified = d.idiaNotified != null ? d.idiaNotified : 0;
        const errored = d.errored != null ? d.errored : 0;
        const info = d.scanned !== undefined
          ? `Tamamlandı. Sorgulanan: ${scanned}, Durum değişen: ${statusChanged}, Idia bildirim: ${idiaNotified}, Hata: ${errored}`
          : 'Sync tetiklendi';
        alert(info);
      } catch (e) {
        alert('Sync hatası: ' + ((e.response && e.response.data && e.response.data.error) || e.message));
      } finally {
        this.syncingAll = false;
      }
    },
    toggleDateRange() {
      const today = new Date();
      if (this.weekActive) {
        this.filters.from = this.isoDate(today);
        this.filters.to = this.isoDate(today);
        this.weekActive = false;
      } else {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 6);
        this.filters.from = this.isoDate(weekAgo);
        this.filters.to = this.isoDate(today);
        this.weekActive = true;
      }
      this.loadList(1);
    },
    async loadList(page) {
      this.loading = true;
      this.page = page;
      try {
        const params = { page, pageSize: this.pageSize };
        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.onlyLost) {
          params.onlyLost = true;
        } else {
          if (this.filters.from) params.from = this.filters.from;
          if (this.filters.to) params.to = this.filters.to;
        }
        const r = await yurticiKargoApi.sentList(params);
        const d = r.data || {};
        this.items = d.items || [];
        this.total = d.total || 0;
      } catch (e) {
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    async toggleDetail(orderId) {
      if (this.expanded[orderId]) {
        this.$delete(this.expanded, orderId);
        return;
      }
      this.$set(this.expanded, orderId, { loading: true, data: null, error: '', syncing: false, lastSync: null });
      try {
        const r = await yurticiKargoApi.statusByOrderId(orderId);
        const entry = this.expanded[orderId];
        if (!entry) return;
        entry.data = r.data;
        entry.loading = false;
      } catch (e) {
        const entry = this.expanded[orderId];
        if (!entry) return;
        entry.error = 'Detay yüklenemedi';
        entry.loading = false;
      }
    },
    async refreshExpandedDetail(orderId) {
      if (!this.expanded[orderId]) return;
      try {
        const r = await yurticiKargoApi.statusByOrderId(orderId);
        const entry = this.expanded[orderId];
        if (!entry) return;
        entry.data = r.data;
      } catch (e) { /* sessiz */ }
    },
    async syncRow(orderId) {
      const entry = this.expanded[orderId];
      if (!entry || entry.syncing) return;
      entry.syncing = true;
      try {
        const r = await yurticiKargoApi.syncStatus(orderId, { paymentType: 0, addHistoricalData: true });
        const d = r.data || {};
        if (d.success) {
          entry.lastSync = d;
          await this.refreshExpandedDetail(orderId);
          await this.loadList(this.page);
        } else {
          alert('Yenileme hatası: ' + (d.error || 'bilinmeyen'));
        }
      } catch (e) {
        alert('Yenileme hatası: ' + ((e.response && e.response.data && e.response.data.error) || e.message));
      } finally {
        entry.syncing = false;
      }
    },
    isFinal(status) {
      return ['DLV', 'CNL', 'ISC', 'BI'].includes(status);
    },
    isCancelable(row) {
      if (this.isRowFailed(row)) return false;
      if (!row.ykOperationStatus) return true;
      return ['NOP', 'ISR'].includes(row.ykOperationStatus);
    },
    isReshipable(row) {
      if (row.ykIsReturned !== true) return false;
      if (row.ykIsReturnedAuto === false) return true;
      return row.ykRejectStatus === '9' || row.ykRejectStatus === '10';
    },
    reshipTooltip(row) {
      if (this.isReshipable(row)) return 'Yeni barkod ile tekrar gönder';
      if (row.ykIsReturned !== true) return 'Sadece iade durumundaki kargolar için aktiftir';
      const s = row.ykRejectStatus || '?';
      return `Kargo iade aşamasında (rejectStatus=${s}). Kargo fiziksel olarak elinize ulaştığında (rejectStatus=9 veya 10) basın.`;
    },
    canMarkReturned(row) {
      if (row.ykIsReturned === true) return false;
      if (this.isFinal(row.ykOperationStatus)) return false;
      return true;
    },
    openMarkReturnedModal(row) {
      if (!this.canMarkReturned(row)) return;
      this.markReturnedModal = {
        open: true, step: 1, orderId: row.orderId, barcode: row.barcode,
        input: '', error: '', busy: false
      };
      this.$nextTick(() => {
        if (this.$refs.markReturnedBarcodeInput) this.$refs.markReturnedBarcodeInput.focus();
      });
    },
    closeMarkReturnedModal() {
      if (this.markReturnedModal.busy) return;
      this.markReturnedModal.open = false;
    },
    validateMarkReturnedBarcode() {
      const entered = (this.markReturnedModal.input || '').trim();
      if (!entered) {
        this.markReturnedModal.error = 'Barkod boş olamaz';
        return;
      }
      if (entered.toUpperCase() !== this.markReturnedModal.barcode.toUpperCase()) {
        this.markReturnedModal.error = `Barkod eşleşmiyor. Beklenen: ${this.markReturnedModal.barcode}`;
        return;
      }
      this.markReturnedModal.error = '';
      this.markReturnedModal.step = 2;
    },
    async confirmMarkReturned() {
      this.markReturnedModal.busy = true;
      this.markReturnedModal.error = '';
      try {
        const r = await yurticiKargoApi.markAsReturned(this.markReturnedModal.orderId, {
          barcodeConfirm: this.markReturnedModal.barcode
        });
        const d = r.data || {};
        if (d.success) {
          this.markReturnedModal.open = false;
          await this.loadList(this.page);
          await this.refreshExpandedDetail(this.markReturnedModal.orderId);
          alert(d.message || 'Kargo geri dönüşü olarak işaretlendi. "Yeni Barkod Oluştur" butonu artık aktif.');
        } else {
          this.markReturnedModal.error = d.error || 'İşaretleme başarısız';
        }
      } catch (e) {
        this.markReturnedModal.error = (e.response && e.response.data && e.response.data.error) || e.message;
      } finally {
        this.markReturnedModal.busy = false;
      }
    },
    openLostModal(row) {
      this.lostModal = {
        open: true, step: 1, orderId: row.orderId, barcode: row.barcode,
        input: '', error: '', busy: false, isLost: row.ykIsLost === true
      };
      this.$nextTick(() => {
        if (this.$refs.lostBarcodeInput) this.$refs.lostBarcodeInput.focus();
      });
    },
    closeLostModal() {
      if (this.lostModal.busy) return;
      this.lostModal.open = false;
    },
    validateLostBarcode() {
      const entered = (this.lostModal.input || '').trim();
      if (!entered) {
        this.lostModal.error = 'Barkod boş olamaz';
        return;
      }
      if (entered.toUpperCase() !== this.lostModal.barcode.toUpperCase()) {
        this.lostModal.error = `Barkod eşleşmiyor. Beklenen: ${this.lostModal.barcode}`;
        return;
      }
      this.lostModal.error = '';
      this.lostModal.step = 2;
    },
    async confirmSetLost() {
      this.lostModal.busy = true;
      this.lostModal.error = '';
      try {
        const r = await yurticiKargoApi.setLostStatus(this.lostModal.orderId, {
          barcodeConfirm: this.lostModal.barcode,
          isLost: !this.lostModal.isLost
        });
        const d = r.data || {};
        if (d.success) {
          this.lostModal.open = false;
          await this.loadList(this.page);
          await this.refreshExpandedDetail(this.lostModal.orderId);
        } else {
          this.lostModal.error = d.error || 'İşlem başarısız';
        }
      } catch (e) {
        this.lostModal.error = (e.response && e.response.data && e.response.data.error) || e.message;
      } finally {
        this.lostModal.busy = false;
      }
    },
    openReshipModal(row) {
      if (!this.isReshipable(row)) return;
      this.reshipModal = {
        open: true, step: 1, orderId: row.orderId, barcode: row.barcode,
        input: '', error: '', busy: false, newBarcode: '', printData: null
      };
      this.$nextTick(() => {
        if (this.$refs.reshipBarcodeInput) this.$refs.reshipBarcodeInput.focus();
      });
    },
    closeReshipModal() {
      if (this.reshipModal.busy) return;
      this.reshipModal.open = false;
    },
    validateReshipBarcode() {
      const entered = (this.reshipModal.input || '').trim();
      if (!entered) {
        this.reshipModal.error = 'Barkod boş olamaz';
        return;
      }
      if (entered.toUpperCase() !== this.reshipModal.barcode.toUpperCase()) {
        this.reshipModal.error = `Barkod eşleşmiyor. Beklenen: ${this.reshipModal.barcode}`;
        return;
      }
      this.reshipModal.error = '';
      this.reshipModal.step = 2;
    },
    async confirmReship() {
      this.reshipModal.busy = true;
      this.reshipModal.error = '';
      try {
        const r = await yurticiKargoApi.reshipOrder(this.reshipModal.orderId, {
          barcodeConfirm: this.reshipModal.barcode,
          paymentType: 0
        });
        const d = r.data || {};
        if (d.success) {
          await this.loadList(this.page);
          await this.refreshExpandedDetail(this.reshipModal.orderId);
          this.reshipModal.newBarcode = d.newBarcode;
          this.reshipModal.printData = d.printData || null;
          this.reshipModal.step = 3;
          this.reshipModal.error = '';
        } else {
          this.reshipModal.error = d.error || 'Yeni barkod oluşturulamadı';
        }
      } catch (e) {
        this.reshipModal.error = (e.response && e.response.data && e.response.data.error) || e.message;
      } finally {
        this.reshipModal.busy = false;
      }
    },
    printNewBarcode() {
      if (!this.reshipModal.printData) {
        alert('Yazdırma verisi yok');
        return;
      }
      sessionStorage.setItem('barcodeprint', JSON.stringify([this.reshipModal.printData]));
      this.reshipModal.open = false;
      window.open('order/barcodeprint', '_blank');
    },
    openCancelModal(row) {
      if (!this.isCancelable(row)) return;
      this.cancelModal = {
        open: true, step: 1, orderId: row.orderId, barcode: row.barcode,
        input: '', error: '', busy: false
      };
      this.$nextTick(() => {
        if (this.$refs.cancelBarcodeInput) this.$refs.cancelBarcodeInput.focus();
      });
    },
    closeCancelModal() {
      if (this.cancelModal.busy) return;
      this.cancelModal.open = false;
    },
    validateBarcode() {
      const entered = (this.cancelModal.input || '').trim();
      if (!entered) {
        this.cancelModal.error = 'Barkod boş olamaz';
        return;
      }
      if (entered.toUpperCase() !== this.cancelModal.barcode.toUpperCase()) {
        this.cancelModal.error = `Barkod eşleşmiyor. Beklenen: ${this.cancelModal.barcode}`;
        return;
      }
      this.cancelModal.error = '';
      this.cancelModal.step = 2;
    },
    async confirmCancel() {
      this.cancelModal.busy = true;
      this.cancelModal.error = '';
      try {
        const r = await yurticiKargoApi.cancelOrderById(this.cancelModal.orderId, {
          barcodeConfirm: this.cancelModal.barcode,
          paymentType: 0
        });
        const d = r.data || {};
        if (d.success) {
          this.cancelModal.open = false;
          await this.loadList(this.page);
          await this.refreshExpandedDetail(this.cancelModal.orderId);
          alert('Kargo başarıyla iptal edildi.');
        } else {
          this.cancelModal.error = d.error || 'İptal başarısız';
        }
      } catch (e) {
        this.cancelModal.error = (e.response && e.response.data && e.response.data.error) || e.message;
      } finally {
        this.cancelModal.busy = false;
      }
    },
    statusClass(s, row) {
      if (row && this.isRowFailed(row)) return 'st-failed';
      if (row && row.ykIsReturned === true) return 'st-amber';
      const m = { IND: 'st-blue', DLV: 'st-green', CNL: 'st-red', ISC: 'st-red', NOP: 'st-gray', ISR: 'st-yellow', BI: 'st-red' };
      return m[s] || 'st-gray';
    },
    statusLabel(row) {
      if (this.isRowFailed(row)) return row.ykErrorMessage ? 'Hatalı' : 'Gönderilmemiş';
      if (row.ykIsReturned === true) return '↩️ İade Sürecinde';
      return this.statusText(row.ykOperationStatus);
    },
    statusText(code) {
      const map = {
        NOP: 'İşlem Görmemiş',
        ISR: 'İrsaliye Düzenlendi',
        IND: 'Teslimatta',
        DLV: 'Teslim Edildi',
        CNL: 'İptal Edildi',
        ISC: 'Daha Önce İptal',
        BI: 'Fatura İptal'
      };
      if (!code) return '—';
      return map[code] || code;
    },
    isRowFailed(row) {
      return !row.ykSentAt && !!row.barcode;
    },
    async retryRow(row) {
      if (this.retryingId) return;
      this.retryingId = row.orderId;
      try {
        const r = await yurticiKargoApi.sendOrderByBarcode({ barcode: row.barcode, paymentType: 0 });
        const d = r.data || {};
        if (d.success) {
          await this.loadList(this.page);
          await this.refreshExpandedDetail(row.orderId);
        } else {
          alert('Tekrar gönderim başarısız: ' + (d.error || 'bilinmeyen'));
          await this.loadList(this.page);
        }
      } catch (e) {
        alert('Tekrar gönderim hatası: ' + ((e.response && e.response.data && e.response.data.error) || e.message));
      } finally {
        this.retryingId = null;
      }
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(dt.getDate())}.${pad(dt.getMonth() + 1)}.${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    },
    formatYkDateTime(date, time) {
      if (!date) return '—';
      const d = `${date.slice(6, 8)}.${date.slice(4, 6)}.${date.slice(0, 4)}`;
      const t = time ? `${time.slice(0, 2)}:${time.slice(2, 4)}` : '';
      return `${d} ${t}`.trim();
    }
  }
};
</script>

<style scoped>
.cl-wrap { max-width: 1400px; margin: 24px auto; padding: 24px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1f2937; }

.cl-guard { background: #fef3c7; border-left: 4px solid #d4a017; padding: 16px 20px; border-radius: 6px; color: #78350f; }
.cl-guard p { margin: 8px 0 0 0; font-size: 13px; }
.cl-guard code { background: #fff; padding: 2px 6px; border-radius: 3px; font-size: 12px; }

.cl-header h1 { font-size: 22px; margin: 0 0 6px 0; color: #111; }
.cl-sub { font-size: 13px; color: #555; margin: 0 0 20px 0; }

.cl-tabs { display: flex; gap: 4px; margin-bottom: 12px; border-bottom: 2px solid #e5e7eb; }
.cl-tab { padding: 10px 18px; background: transparent; border: none; border-bottom: 2px solid transparent; margin-bottom: -2px; cursor: pointer; font-size: 14px; font-weight: 500; color: #6b7280; font-family: inherit; display: inline-flex; align-items: center; gap: 6px; }
.cl-tab:hover { color: #1d4ed8; background: #f3f4f6; }
.cl-tab-active { color: #1d4ed8; border-bottom-color: #1d4ed8; background: #fff; }
.cl-tab-badge { background: #dc2626; color: white; font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 10px; min-width: 20px; text-align: center; }

.cl-overdue-warn { background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 12px; }
.cl-row-overdue:hover { background: #fef2f2 !important; }
.cl-row-highlight { background: #fef9c3 !important; }
.cl-overdue-badge { background: #dc2626; color: white; font-size: 12px; font-weight: 700; padding: 3px 9px; border-radius: 10px; white-space: nowrap; display: inline-block; }

.cl-row-lost { background: #fee2e2 !important; }
.cl-row-lost:hover { background: #fecaca !important; }

.cl-only-lost { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; font-weight: 500; padding: 8px 12px; border: 1px solid #fca5a5; border-radius: 4px; background: #fef2f2; color: #991b1b; user-select: none; align-self: flex-end; }
.cl-only-lost:hover { background: #fee2e2; border-color: #f87171; }
.cl-only-lost input { width: 16px; height: 16px; min-width: 16px; margin: 0; padding: 0; cursor: pointer; accent-color: #dc2626; }

.cl-success { text-align: center; padding: 18px 8px 10px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; }
.cl-success-icon { width: 48px; height: 48px; line-height: 48px; margin: 0 auto 8px; background: #16a34a; color: white; border-radius: 50%; font-size: 28px; font-weight: 700; }
.cl-success h4 { margin: 0 0 8px; color: #166534; font-size: 16px; }
.cl-success p { margin: 4px 0; font-size: 13px; color: #15803d; }
.cl-new-barcode { font-size: 16px; color: #166534; background: white; padding: 3px 10px; border-radius: 4px; border: 1px solid #bbf7d0; }

.cl-filters { display: flex; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; align-items: end; background: #fff; padding: 14px; border: 1px solid #e5e7eb; border-radius: 8px; }
.cl-filter { display: flex; flex-direction: column; gap: 4px; }
.cl-filter label { font-size: 11px; color: #4b5563; font-weight: 500; }
.cl-filter input, .cl-filter select { padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; font-family: inherit; min-width: 180px; }
.cl-filter input:focus, .cl-filter select:focus { outline: none; border-color: #1d4ed8; }

.cl-btn { padding: 8px 14px; border: none; border-radius: 4px; font-size: 13px; cursor: pointer; font-weight: 500; font-family: inherit; }
.cl-btn.primary { background: #1d4ed8; color: white; }
.cl-btn.primary:hover { background: #1e40af; }
.cl-btn.primary:disabled { background: #9ca3af; cursor: not-allowed; }
.cl-btn.ghost { background: white; border: 1px solid #d1d5db; color: #374151; }
.cl-btn.ghost:hover { background: #f9fafb; }

.cl-table-wrap { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-bottom: 16px; }
.cl-table { width: 100%; border-collapse: collapse; }
.cl-table th { background: #f9fafb; padding: 10px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #4b5563; border-bottom: 1px solid #e5e7eb; }
.cl-table td { padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #f3f4f6; }
.cl-table tr:hover { background: #f9fafb; }
/* cl-row-selected: tanım expandable detail bloğunun yanında — burası kaldırıldı */
.cl-empty { text-align: center; color: #9ca3af; padding: 30px !important; }
.mono { font-family: "SF Mono", Monaco, monospace; font-size: 12px; }

.cl-status { padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: 600; white-space: nowrap; display: inline-block; }
.st-green { background: #d1fae5; color: #065f46; }
.st-blue { background: #dbeafe; color: #1e40af; }
.st-red { background: #fee2e2; color: #991b1b; }
.st-yellow { background: #fef3c7; color: #92400e; }
.st-gray { background: #f3f4f6; color: #6b7280; }
.st-failed { background: #fef2f2; color: #991b1b; border: 1px solid #fca5a5; }
.st-amber { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }

.cl-row-failed { background: #fef2f2; }
.cl-row-failed:hover { background: #fee2e2; }
.cl-row-err { font-size: 11px; color: #991b1b; margin-top: 3px; max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cl-retry { margin-left: 6px; font-size: 11px; padding: 6px 10px; }

.cl-btn.danger { background: #dc2626; color: white; }
.cl-btn.danger:hover { background: #b91c1c; }
.cl-btn.danger:disabled { background: #9ca3af; cursor: not-allowed; }
.cl-btn.warning { background: #ea580c; color: white; }
.cl-btn.warning:hover { background: #c2410c; }
.cl-btn.warning:disabled { background: #9ca3af; cursor: not-allowed; }
.cl-reship { margin-left: 6px; font-size: 11px; padding: 6px 10px; }
.cl-btn.disabled { background: #f3f4f6; color: #9ca3af; cursor: not-allowed; border: 1px solid #e5e7eb; opacity: 0.6; }
.cl-btn.amber { background: #d97706; color: white; }
.cl-btn.amber:hover { background: #b45309; }
.cl-btn.amber:disabled { background: #9ca3af; cursor: not-allowed; }

/* === Row aksiyon butonları (elit/minimal outline tarzı) === */
.cl-row-actions {
  display: inline-grid;
  grid-template-columns: repeat(3, minmax(108px, 1fr));
  gap: 5px;
  width: fit-content;
}
.cl-row-actions .cl-btn {
  margin: 0 !important;
  padding: 5px 8px;
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.1px;
  line-height: 1.35;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
  text-align: center;
  box-shadow: 0 1px 0 rgba(0,0,0,0.02);
}

/* Ghost — Detay (neutral, outlined) */
.cl-row-actions .cl-btn.ghost {
  background: #ffffff;
  border-color: #e5e7eb;
  color: #374151;
}
.cl-row-actions .cl-btn.ghost:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #111827;
}

/* Ghost Active — Detayı Kapat (açık halde, daha belirgin) */
.cl-row-actions .cl-btn.ghost-active {
  background: #1f2937;
  border-color: #1f2937;
  color: #ffffff;
}
.cl-row-actions .cl-btn.ghost-active:hover:not(:disabled) {
  background: #111827;
  border-color: #111827;
}

/* Primary — Tekrar Dene (blue solid) */
.cl-row-actions .cl-btn.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}
.cl-row-actions .cl-btn.primary:hover:not(:disabled) {
  background: #1d4ed8;
  border-color: #1d4ed8;
}

/* Danger — İptal Et (red solid) */
.cl-row-actions .cl-btn.danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #ffffff;
}
.cl-row-actions .cl-btn.danger:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}

/* Amber — Kargo Geri Döndü (amber solid) */
.cl-row-actions .cl-btn.amber {
  background: #d97706;
  border-color: #d97706;
  color: #ffffff;
}
.cl-row-actions .cl-btn.amber:hover:not(:disabled) {
  background: #b45309;
  border-color: #b45309;
}

/* Warning — Yeni Barkod (orange solid) */
.cl-row-actions .cl-btn.warning {
  background: #ea580c;
  border-color: #ea580c;
  color: #ffffff;
}
.cl-row-actions .cl-btn.warning:hover:not(:disabled) {
  background: #c2410c;
  border-color: #c2410c;
}

/* Lost — Kayıp İşaretle (red outline, distinct from İptal Et's solid red) */
.cl-row-actions .cl-btn.lost {
  background: #ffffff;
  border-color: #fca5a5;
  color: #b91c1c;
}
.cl-row-actions .cl-btn.lost:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}

/* Lost-active — Kayıp olarak işaretlenmiş, geri al butonu (dolu kırmızı, açıkça aktif görünür) */
.cl-row-actions .cl-btn.lost-active {
  background: #fee2e2;
  border-color: #dc2626;
  color: #991b1b;
  font-weight: 600;
}
.cl-row-actions .cl-btn.lost-active:hover:not(:disabled) {
  background: #fecaca;
  border-color: #b91c1c;
}

/* Disabled — herhangi bir renk için */
.cl-row-actions .cl-btn.disabled,
.cl-row-actions .cl-btn:disabled {
  background: #fafafa !important;
  color: #c0c4cc !important;
  border-color: #f0f0f0 !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  opacity: 1 !important;
}
.cl-cancel { margin-left: 6px; font-size: 11px; padding: 6px 10px; }

.cl-modal-backdrop { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; }
.cl-modal { background: white; border-radius: 8px; width: 100%; max-width: 480px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); overflow: hidden; }
.cl-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid #e5e7eb; background: #fef2f2; }
.cl-modal-head h3 { font-size: 15px; margin: 0; color: #991b1b; }
.cl-modal-body { padding: 18px; }
.cl-warn { background: #fef3c7; border-left: 4px solid #d97706; padding: 10px 12px; border-radius: 4px; font-size: 12px; color: #78350f; margin: 0 0 16px 0; }
.cl-modal-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 6px; }
.cl-modal-input { width: 100%; padding: 10px 12px; border: 2px solid #dc2626; border-radius: 4px; font-size: 14px; font-family: "SF Mono", Monaco, monospace; box-sizing: border-box; letter-spacing: 1px; }
.cl-modal-input:focus { outline: none; box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15); }
.cl-modal-err { color: #991b1b; font-size: 12px; margin: 8px 0 0 0; }
.cl-modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 18px; }

.cl-pagination { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-top: 1px solid #e5e7eb; background: #f9fafb; font-size: 13px; color: #6b7280; }

/* === Expandable Detail Row (inline) === */
.cl-row-selected { background: #f5f9ff !important; }
.cl-expanded-row > td { padding: 0 !important; background: #fafbfc; border-bottom: 1px solid #e5e7eb; }
.cl-expanded-row > td > .cl-detail-inline,
.cl-expanded-row > td > .cl-detail-state {
  padding: 18px 22px;
  border-left: 3px solid #2563eb;
  background: #fafbfc;
}
.cl-detail-state { font-size: 13px; color: #6b7280; }

.cl-detail-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid #e5e7eb; }
.cl-detail-title { font-size: 14px; color: #111; font-weight: 600; display: inline-flex; align-items: center; gap: 8px; }
.cl-detail-barcode { font-size: 13px; color: #1d4ed8; background: #eff6ff; padding: 2px 8px; border-radius: 4px; }
.cl-detail-sep { color: #d1d5db; }
.cl-detail-actions { display: flex; gap: 8px; }
.cl-detail-actions .cl-btn { font-size: 12px; padding: 6px 12px; border-radius: 6px; }
.cl-detail-actions .cl-btn.primary-solid {
  background: #2563eb; color: #fff; border: 1px solid #2563eb;
}
.cl-detail-actions .cl-btn.primary-solid:hover:not(:disabled) {
  background: #1d4ed8; border-color: #1d4ed8;
}
.cl-detail-actions .cl-btn.ghost {
  background: #fff; border: 1px solid #e5e7eb; color: #374151;
}
.cl-detail-actions .cl-btn.ghost:hover:not(:disabled) {
  background: #f9fafb; border-color: #d1d5db;
}
.cl-detail-actions .cl-btn.disabled {
  background: #f3f4f6; color: #9ca3af; border: 1px solid #e5e7eb; cursor: not-allowed;
}

.cl-detail-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px 20px; }
.cl-detail-grid > div { display: flex; flex-direction: column; gap: 3px; font-size: 13px; }
.cl-detail-grid label { font-size: 11px; color: #6b7280; font-weight: 500; }
.cl-detail-grid span { color: #111; word-break: break-word; }
.cl-full { grid-column: 1 / -1; }
.cl-err { color: #991b1b; }
.cl-link { color: #1d4ed8; text-decoration: none; font-size: 12px; word-break: break-all; }
.cl-link:hover { text-decoration: underline; }

.cl-return-info { display: flex; flex-wrap: wrap; gap: 6px 12px; align-items: center; }
.cl-return-badge { background: #fef3c7; color: #92400e; padding: 3px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; border: 1px solid #fde68a; }
.cl-return-meta { font-size: 12px; color: #4b5563; }
.cl-return-tag { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 500; }
.cl-return-tag-auto { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
.cl-return-tag-manual { background: #ede9fe; color: #5b21b6; border: 1px solid #ddd6fe; }

.cl-history { margin-top: 20px; padding-top: 16px; border-top: 1px solid #e5e7eb; }
.cl-history h3 { font-size: 14px; margin: 0 0 10px 0; color: #111; }
.cl-history-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.cl-history-table th { background: #f9fafb; padding: 8px; text-align: left; font-weight: 600; color: #4b5563; border-bottom: 1px solid #e5e7eb; }
.cl-history-table td { padding: 8px; border-bottom: 1px solid #f3f4f6; }

@media (max-width: 768px) {
  .cl-detail-grid { grid-template-columns: 1fr; }
}
</style>
